import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import { buildTextFieldSx, fieldLabelRowSx, FIELD_PLACEHOLDER_COLOR } from '../common/formFieldStyles';
import { BRAND } from '../../theme/brandTokens';
import RevenueService from '../../services/revenueService';
import MoloniService from '../../services/moloniService';
import TeamMemberService from '../../services/teamMemberService';
import StoreCustomerService from '../../services/storeCustomerService';
import IssueInvoiceDialog, { CopyInvoiceLinkButton } from '../moloni/IssueInvoiceDialog';
import { formatCurrency } from '../../utils/currencyUtils';
import { describeApiError } from '../../utils/stockErrorUtils';
import { describeInvoiceError } from '../../utils/moloniError';
import { bulkIssuePayload, monthBounds, recentMonthOptions } from './salesListFilters';

const PAGE_SIZE = 50;

const SalesListPanel = ({ storeId, currency, canWrite, invoicing = false, range = null }) => {
  const { t, i18n } = useTranslation();
  const language = (i18n.language || 'pt').slice(0, 2);
  const months = useMemo(() => recentMonthOptions(new Date(), language), [language]);

  const [month, setMonth] = useState(months[0].value);
  const [teamMemberIds, setTeamMemberIds] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [invoiceStatus, setInvoiceStatus] = useState('notIssued');
  const [teamMembers, setTeamMembers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [dialogKey, setDialogKey] = useState(null);
  const customerIds = useMemo(
    () => selectedCustomers.map((customer) => customer.id || customer.customerId),
    [selectedCustomers],
  );
  const customerOptions = useMemo(() => {
    const chosenIds = new Set(customerIds.map(String));
    return [...selectedCustomers, ...customers.filter((customer) => !chosenIds.has(String(customer.id || customer.customerId)))];
  }, [customerIds, customers, selectedCustomers]);

  useEffect(() => {
    TeamMemberService.getTeamMembersByStore(storeId).then((list) => {
      const members = Array.isArray(list) ? list : (list?.items || list?.data || []);
      setTeamMembers(members);
    }).catch(() => setTeamMembers([]));
  }, [storeId]);

  useEffect(() => {
    if (!customerSearch.trim()) return undefined;
    const handle = setTimeout(() => {
      StoreCustomerService.getStoreCustomers(storeId, { search: customerSearch, itemsPerPage: 20 })
        .then((page) => setCustomers(page.data || []))
        .catch(() => setCustomers([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [customerSearch, storeId]);

  const rangeFrom = range?.from;
  const rangeTo = range?.to;
  const bounds = useMemo(
    () => (rangeFrom && rangeTo ? { from: rangeFrom, to: rangeTo } : monthBounds(month)),
    [month, rangeFrom, rangeTo],
  );

  const load = useCallback(async (isActive = () => true) => {
    const data = await RevenueService.getReport(storeId, {
      ...bounds,
      language,
      pageIndex,
      pageSize: PAGE_SIZE,
      teamMemberIds,
      customerIds,
      invoiceStatus: invoicing ? invoiceStatus : 'all',
    });
    if (!isActive()) return;
    setError('');
    setRows(data.rows || data.Rows || []);
    setTotalRows(data.totalRows ?? data.TotalRows ?? 0);
    setSelected([]);
    setSelectAll(false);
  }, [bounds, invoicing, language, pageIndex, storeId, teamMemberIds, customerIds, invoiceStatus]);

  useEffect(() => {
    let cancelled = false;
    load(() => !cancelled).catch((requestError) => {
      if (!cancelled) setError(describeApiError(requestError, t));
    });
    return () => { cancelled = true; };
  }, [load, t]);

  const eligible = rows.filter((row) => !(row.invoiceIssued ?? row.InvoiceIssued));
  const chosen = selectAll ? eligible : rows.filter((row) => selected.includes(row.sourceKey || row.SourceKey));

  const issue = async () => {
    if (chosen.length === 1 && !selectAll) {
      setDialogKey(chosen[0].sourceKey || chosen[0].SourceKey);
      return;
    }

    try {
      setError('');
      const result = await MoloniService.bulk(storeId, bulkIssuePayload({
        bounds,
        teamMemberIds,
        customerIds,
        selectAll,
        sourceKeys: chosen.map((row) => row.sourceKey || row.SourceKey),
      }));
      setProgress(result);
      if (result.jobId || result.JobId) {
        const jobId = result.jobId || result.JobId;
        const timer = setInterval(async () => {
          try {
            const job = await MoloniService.job(storeId, jobId);
            setProgress(job);
            if ((job.status || job.Status) === 'completed') {
              clearInterval(timer);
              load();
            }
          } catch (requestError) {
            clearInterval(timer);
            setError(describeInvoiceError(requestError, t));
          }
        }, 2000);
      } else {
        load();
      }
    } catch (requestError) {
      setError(describeInvoiceError(requestError, t));
    }
  };

  const filterCount = (range ? 0 : 1) + 2 + (invoicing ? 1 : 0);
  const memberName = (id) => {
    const member = teamMembers.find((item) => item.id === id);
    return member ? (member.name || member.userName) : '';
  };
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: `repeat(${filterCount}, minmax(0, 1fr))` },
          gap: 2,
          alignItems: 'end',
          mb: 2,
        }}
      >
        {range ? null : (
          <SalovareSelectField label={t('salesList.month')} value={month} onChange={(event) => { setPageIndex(0); setMonth(event.target.value); }}>
            {months.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </SalovareSelectField>
        )}
        <SalovareSelectField
          label={t('salesList.professionals')}
          multiple
          displayEmpty
          value={teamMemberIds}
          onChange={(event) => { setPageIndex(0); setTeamMemberIds(event.target.value); }}
          renderValue={(value) => (value.length
            ? value.map(memberName).filter(Boolean).join(', ')
            : <Box component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>{t('salesList.all')}</Box>)}
        >
          {teamMembers.map((member) => (
            <MenuItem key={member.id} value={member.id}>{member.name || member.userName}</MenuItem>
          ))}
        </SalovareSelectField>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={fieldLabelRowSx}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{t('salesList.customers')}</Typography>
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={customerOptions}
            value={selectedCustomers}
            filterOptions={(options) => options}
            getOptionLabel={(customer) => customer.name || customer.displayName || ''}
            isOptionEqualToValue={(option, value) => String(option.id || option.customerId) === String(value.id || value.customerId)}
            inputValue={customerSearch}
            onInputChange={(_, value, reason) => {
              if (reason !== 'reset') setCustomerSearch(value);
            }}
            onChange={(_, value) => { setPageIndex(0); setSelectedCustomers(value); }}
            noOptionsText={t('salesList.searchCustomers')}
            disableCloseOnSelect
            renderTags={(value) => (
              <Typography variant="body2" sx={{ pl: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>
                {value.map((customer) => customer.name || customer.displayName).join(', ')}
              </Typography>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedCustomers.length ? '' : t('salesList.searchCustomers')}
                sx={{
                  ...buildTextFieldSx(),
                  '& .MuiOutlinedInput-root': {
                    ...buildTextFieldSx()['& .MuiOutlinedInput-root'],
                    flexWrap: 'nowrap',
                    py: '0 !important',
                  },
                  '& .MuiOutlinedInput-input': { padding: '12px 8px !important', fontSize: '16px' },
                }}
              />
            )}
          />
        </Box>
        {invoicing ? (
          <SalovareSelectField label={t('salesList.invoiceStatus')} value={invoiceStatus} onChange={(event) => { setPageIndex(0); setInvoiceStatus(event.target.value); }}>
            <MenuItem value="notIssued">{t('salesList.notIssued')}</MenuItem>
            <MenuItem value="issued">{t('salesList.issued')}</MenuItem>
            <MenuItem value="all">{t('salesList.all')}</MenuItem>
          </SalovareSelectField>
        ) : null}
      </Box>
      {invoicing ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: BRAND.textMuted }}>
            {t('salesList.selectedCount', { count: chosen.length })}
          </Typography>
          {canWrite ? (
            <PrimaryActionButton onClick={issue} disabled={chosen.length === 0}>
              {t('salesList.issueSelected')}
            </PrimaryActionButton>
          ) : null}
        </Box>
      ) : null}
      {error ? <Typography color="error" sx={{ mb: 1 }}>{error}</Typography> : null}
      {progress ? (
        <Typography sx={{ mb: 1 }}>
          {t('salesList.progress', {
            issued: progress.issuedCount ?? progress.IssuedCount ?? 0,
            skipped: progress.skippedCount ?? progress.SkippedCount ?? 0,
            failed: progress.failedCount ?? progress.FailedCount ?? 0,
          })}
        </Typography>
      ) : null}
      <Box sx={{ overflowX: 'auto', border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: BRAND.background, '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
            {invoicing ? (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectAll}
                  onChange={(event) => setSelectAll(event.target.checked)}
                  inputProps={{ 'aria-label': t('salesList.selectAll') }}
                />
              </TableCell>
            ) : null}
            {['date', 'professional', 'customer', 'vat', 'services', 'products', 'total'].map((column) => (
              <TableCell key={column} align={column === 'total' ? 'right' : 'left'}>{t(`revenueReport.columns.${column}`)}</TableCell>
            ))}
            {invoicing ? <TableCell>{t('salesList.invoiceStatus')}</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={invoicing ? 9 : 7}>
                <Typography variant="body2" color="text.secondary">{t('salesList.empty')}</Typography>
              </TableCell>
            </TableRow>
          ) : rows.map((row) => {
            const key = row.sourceKey || row.SourceKey;
            const issued = Boolean(row.invoiceIssued ?? row.InvoiceIssued);
            return (
              <TableRow key={key}>
                {invoicing ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={!issued && (selectAll || selected.includes(key))}
                      disabled={issued}
                      onChange={(event) => {
                        setSelectAll(false);
                        setSelected((current) => (
                          event.target.checked ? [...current, key] : current.filter((item) => item !== key)
                        ));
                      }}
                    />
                  </TableCell>
                ) : null}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date || row.Date}</TableCell>
                <TableCell>{row.professional || row.Professional || '—'}</TableCell>
                <TableCell>{row.customer || row.Customer || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.customerVatNumber || row.CustomerVatNumber || '—'}</TableCell>
                <TableCell>{row.services || row.Services || row.description || row.Description || '—'}</TableCell>
                <TableCell>{row.products || row.Products || '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.totalPaid ?? row.TotalPaid ?? 0, currency)}</TableCell>
                {invoicing ? (
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: issued ? 'success.main' : BRAND.textMuted, fontWeight: issued ? 600 : 400 }}>
                      {issued
                        ? `${t('moloni.issued')} ${row.moloniDocumentNumber || row.MoloniDocumentNumber || ''}`
                        : t('moloni.notIssued')}
                    </Typography>
                    {issued ? <CopyInvoiceLinkButton storeId={storeId} sourceKey={key} /> : null}
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button color="inherit" disabled={pageIndex === 0} onClick={() => setPageIndex((current) => current - 1)}>
          {t('salesList.previous')}
        </Button>
        <Button color="inherit" disabled={(pageIndex + 1) * PAGE_SIZE >= totalRows} onClick={() => setPageIndex((current) => current + 1)}>
          {t('salesList.next')}
        </Button>
      </Box>
      {invoicing ? (
      <IssueInvoiceDialog
        open={Boolean(dialogKey)}
        storeId={storeId}
        sourceKey={dialogKey}
        onClose={() => setDialogKey(null)}
        onIssued={() => load()}
      />
      ) : null}
    </Box>
  );
};

export default SalesListPanel;
